import router from '@core/router';
import BaseModel from '@core/model';
import store from '@core/store';

import EventTypeEnum from '@constants/enums/event/type';
import NotificationGroupEnum from '@constants/enums/notification/group';
import NotificationObjectClassEnum from '@constants/enums/notification/objectClass';
import NotificationTypeEnum from '@constants/enums/notification/type';

import NotificationApi from '@packages/notifications/api';

import clock from '@plugins/dayjs';
import i18n from '@plugins/i18n';

import { User, Investor, Adviser, Offer, Proposal, Shortlist, MediaInvestor, MediaAdviser, Event } from '@/models';

export class Notification extends BaseModel {
    static name = 'Notification';
    static entity = 'notifications';
    static Api = NotificationApi;
    static runSetup = true;

    async setupLogic() {
        if (!(this.event instanceof Event)) {
            if (this.additional_data && this.additional_data.event) {
                this.event = new Event(this.additional_data.event);
            } else {
                this.event = new Event();
            }
            this.$save();
        }
    }

    static fields() {
        return {
            ...super.fields(),
            //
            id: this.string(null).nullable(),
            type: this.enum(NotificationTypeEnum).nullable(),
            title: this.string(null).nullable(),
            body: this.string(null).nullable(),
            object: this.attr(null).nullable(),
            object_id: this.string(null).nullable(),
            object_class: this.enum(NotificationObjectClassEnum).nullable(),
            synthetic: this.boolean(false).nullable(),
            event_at: this.string(null).nullable(),
            created_at: this.string(null).nullable(),
            viewed_at: this.string(null).nullable(),
            deleted_at: this.string(null).nullable(),
            user_id: this.string(null).nullable(),
            user: this.belongsTo(User, 'user_id'),
            additional_data: this.attr(null).nullable(),
            event: this.model(Event, null).nullable()
        };
    }

    static mock() {
        return {
            id: faker => faker.string.uuid(),
            type: faker => faker.helpers.arrayElement(Object.values(NotificationTypeEnum)),
            object_class: (faker, item) => {
                let objectClasses = Object.values(NotificationObjectClassEnum);
                const objectClass = item.type ? item.type.split('_')[0] : null;

                if (objectClass) {
                    return objectClass;
                }

                return faker => faker.helpers.arrayElement(objectClasses);
            },
            object: (faker, item) => {
                item = new Notification(item);
                const objectModel = item.object_model;
                if (!objectModel) return null;
                return objectModel.Api;
            },
            object_id: (faker, item) => item.object?.id || null,
            title: (faker, item) => {
                return i18n.t(`enums.notification_type.${item.type}`);
            },
            body: (faker, item) => {
                let body = '';
                if (item.object && item.object.name) {
                    body += item.object.name + ' ';
                }
                if (item.object && item.object.offer && item.object.offer.name) {
                    body += item.object.offer.name + ' ';
                }
                body += faker.lorem.paragraph(3);
                return body;
            },
            created_at: faker => faker.date.recent({ days: 90 }).toISOString(),
            event_at: faker => faker.date.recent({ days: 90 }).toISOString(),
            viewed_at: faker => faker.helpers.arrayElement([null, faker.date.recent({ days: 90 }).toISOString()]),
            user: User.Api,
            user_id: (faker, item) => item.user?.id || null
        };
    }

    /**
     * Extracts the notification group from a given notification type.
     * This function assumes that every notification type is suffixed with a group name.
     * For example, a notification type of 'PROPOSAL_CREATED_INVESTOR'
     * would correspond to a notification group of 'INVESTOR'.
     *
     * @param {String} notificationType - The notification type from NotificationTypeEnum.
     * @returns {String|null} The corresponding group from NotificationGroupEnum, or null if not found.
     */
    static getGroupFromNotificationType(notificationType) {
        if (!notificationType || !Object.values(NotificationTypeEnum).includes(notificationType)) {
            return null;
        }

        // Sort notification groups by length in descending order. This is crucial to ensure
        // the longest possible match is found first, preventing incorrect matches
        // for groups that are substrings of others.
        const notificationGroups = Object.values(NotificationGroupEnum).sort((a, b) => b.length - a.length);

        const group = notificationGroups.find(group => notificationType.endsWith(`_${group}`));

        return group || NotificationGroupEnum.GENERAL;
    }

    /**
     * Extracts the event type from a given notification type.
     * This function assumes that every notification type is prefixed with an event type.
     * For example, a notification type of 'INVESTOR_MEDIA_CREATED_PRIMARY_CONTACT'
     * would correspond to an event type of 'INVESTOR_MEDIA_CREATED'.
     *
     * @param {String} notificationType - The notification type from NotificationTypeEnum.
     * @returns {String|null} The corresponding event type from EventTypeEnum, or null if not found.
     */
    static getEventTypeFromNotificationType(notificationType) {
        if (!notificationType || !Object.values(NotificationTypeEnum).includes(notificationType)) {
            return null;
        }

        // Sort event types by length in descending order. This is crucial to ensure
        // the longest possible match is found first, preventing incorrect matches
        // for event types that are prefixes of others (e.g., 'EVENT' and 'EVENT_MORE_SPECIFIC').
        const eventTypes = Object.values(EventTypeEnum).sort((a, b) => b.length - a.length);

        const eventType = eventTypes.find(event => notificationType.startsWith(event));

        return eventType || null;
    }

    get is_unread() {
        return this.viewed_at === null;
    }

    get is_read() {
        return this.viewed_at !== null;
    }

    get is_deleted() {
        return this.deleted_at !== null;
    }

    get time_of_creation() {
        if (!this.created_at) {
            return null;
        }

        return clock(this.created_at).fromNow();
    }

    get time_of_event() {
        if (!this.event_at) {
            return null;
        }

        return clock(this.event_at).fromNow();
    }

    get object_model() {
        switch (this.object_class) {
            case NotificationObjectClassEnum.INVESTOR:
            case NotificationObjectClassEnum.INVESTOR_MEDIA_LIBRARY:
                return Investor;
            case NotificationObjectClassEnum.INVESTOR_MEDIA:
                return MediaInvestor;
            case NotificationObjectClassEnum.ADVISER:
                return Adviser;
            case NotificationObjectClassEnum.ADVISER_MEDIA:
                return MediaAdviser;
            case NotificationObjectClassEnum.OFFER:
                return Offer;
            case NotificationObjectClassEnum.PROPOSAL:
                return Proposal;
            case NotificationObjectClassEnum.SHORTLIST:
                return Shortlist;
            default:
                return null;
        }
    }

    get object_route() {
        let route = null;

        if (this.object_id) {
            if (this.object_class === NotificationObjectClassEnum.INVESTOR) {
                route = {
                    name: 'Settings',
                    query: { organisation_id: this.object_id, organisation_type: 'INVESTOR' }
                };
            } else if (this.object_class === NotificationObjectClassEnum.INVESTOR_MEDIA_LIBRARY) {
                route = {
                    name: 'Library',
                    params: { library: 'investor' },
                    query: { id: this.object_id, type: 'investor' }
                };
            } else if (this.object_class === NotificationObjectClassEnum.INVESTOR_MEDIA) {
                route = {
                    name: 'Library',
                    params: { library: 'investor' },
                    query: { id: this.object_id, type: 'investor', media: this.event.media_id }
                };
            } else if (this.object_class === NotificationObjectClassEnum.ADVISER) {
                route = {
                    name: 'Settings',
                    query: { organisation_id: this.object_id, organisation_type: 'ADVISER' }
                };
            } else if (this.object_class === NotificationObjectClassEnum.ADVISER_MEDIA) {
                route = {
                    name: 'Library',
                    params: { library: 'adviser' },
                    query: { id: this.object_id, type: 'adviser', media: this.event.media_id }
                };
            } else if (this.object_class === NotificationObjectClassEnum.OFFER) {
                route = {
                    name: 'Offer',
                    query: { offer_id: this.object_id }
                };
            } else if (this.object_class === NotificationObjectClassEnum.PROPOSAL) {
                if (this.event.has_viewable_proposal) {
                    route = {
                        name: 'Proposals',
                        query: { proposal: this.object_id }
                    };
                }
            } else if (this.object_class === NotificationObjectClassEnum.SHORTLIST) {
                route = {
                    name: 'Shortlist',
                    query: { shortlist_id: this.object_id }
                };
            }
        }

        if (!route) {
            return null;
        }

        const resolvedRoute = router.resolve(route);

        if (resolvedRoute && resolvedRoute.route && resolvedRoute.route.name) {
            return resolvedRoute.route;
        }

        return null;
    }

    static updateNotificationCount() {
        return store.dispatch('app/setUnreadNotificationsCount', this.query().where('viewed_at', null).count());
    }

    static async $update(id) {
        const response = await super.$update(id);

        this.updateNotificationCount();

        return response;
    }

    static async $remove(id) {
        const response = await super.$remove(id);

        this.updateNotificationCount();

        return response;
    }

    async $markAsRead() {
        this.$updating = true;
        this.$saving = ['viewed_at'];

        return await this.constructor.$markAsRead(this.id).finally(() => {
            this.$updating = false;
            this.$saving = [];
        });
    }

    static async $markAsRead(id) {
        if (!id) {
            throw new ReferenceError(`Invalid ID when calling $markAsRead on model ${this.name}`);
        }

        const response = await this.api.read(id, true);

        if (response.data?.id) {
            await this.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        }

        this.updateNotificationCount();

        return this.findBy(id);
    }

    async $markAsUnread() {
        this.$updating = true;
        this.$saving = ['viewed_at'];

        return await this.constructor.$markAsUnread(this.id).finally(() => {
            this.$updating = false;
            this.$saving = [];
        });
    }

    static async $markAsUnread(id) {
        if (!id) {
            throw new ReferenceError(`Invalid ID when calling $markAsUnread on model ${this.name}`);
        }

        const response = await this.api.read(id, false);

        if (response.data?.id) {
            await this.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        }

        this.updateNotificationCount();

        return this.findBy(id);
    }

    static async $markAllAsRead() {
        const mostRecent = Notification.query().orderBy('created_at', 'desc').first();

        if (!mostRecent) {
            throw new Error('No notifications found to mark as read');
        }

        const response = await this.api.read_before(clock(mostRecent.created_at).add(1, 'second').toISOString());

        this.updateNotificationCount();

        return response;
    }

    static async $clearAll() {
        const mostRecent = Notification.query().orderBy('created_at', 'desc').first();

        if (!mostRecent) {
            throw new Error('No notifications found to clear');
        }

        const response = await this.api.clear_before(clock(mostRecent.created_at).add(1, 'second').toISOString());

        this.updateNotificationCount();

        return response;
    }
}

export default Notification;
